<script setup lang="ts">
    import { AisConfigure } from 'vue-instantsearch/vue3/es';

    interface SearchResultsProps {
        data: SearchResultsWidgetData;
    }

    const props = defineProps<SearchResultsProps>();

    const { algoliaIndex } = useAlgolia();

    const limit = props.data.limit || undefined;
    const numericFilters: string[] = [];

    const configure = computed(() => {
        const configure: {
            query?: string;
            categories?: string;
            filters?: string;
            refinementList?: [string, string[]][];
            sortBy?: string;
        } = {};
        if (!props.data.search_query?.data?.query) return configure;
        const searchParams = new URLSearchParams(props.data.search_query.data.query);

        const query = searchParams.get('query');
        if (query) configure.query = query;

        const sort = searchParams.get(ALGOLIA.SORT);
        if (sort) configure.sortBy = `${algoliaIndex.indexName}_${sort}`;

        const parts: string[] = [];

        const category = searchParams.get(ALGOLIA.CATEGORY);
        if (category) {
            configure.categories = category.replaceAll('>', ALGOLIA.CATEGORY_SEPARATOR);
            const level = Math.min(configure.categories.match(/>/g)?.length ?? 0, ALGOLIA.HIERARCHY_MENU_ITEMS.length - 1);
            parts.push(`${ALGOLIA.HIERARCHY_MENU_ITEMS[level]}:"${configure.categories}"`);
        }

        const reservedKeys = ['query', ALGOLIA.CATEGORY, ALGOLIA.SORT];
        configure.refinementList = [...searchParams.entries()]
            .filter(([key]) => !reservedKeys.includes(key))
            .map(([key, value]) => [key, value.split(',').map((v) => v.trim())]);

        configure.refinementList.forEach(([key, value]) => {
            const keyValueParts = value.map((valuePart) => `${key}:"${valuePart.trim()}"`);
            parts.push(`(${keyValueParts.join(' OR ')})`);
        });

        const addPriceLimit = (limit: string) => {
            parts.push(limit);
            numericFilters.push(limit);
        };
        if (props.data.min_price) addPriceLimit(`price >= ${props.data.min_price}`);
        if (props.data.max_price) addPriceLimit(`price <= ${props.data.max_price}`);

        if (parts.length) configure.filters = parts.join(' AND ');

        return configure;
    });

    const activeIndex = computed(() => configure.value.sortBy || algoliaIndex.indexName);

    const initialUiState = computed(() => ({
        [activeIndex.value]: {
            query: configure.value.query,
            sortBy: configure.value.sortBy,
            hierarchicalMenu: configure.value.categories
                ? {
                      [ALGOLIA.HIERARCHY_MENU_ITEMS[0]]: configure.value.categories.split(ALGOLIA.CATEGORY_SEPARATOR),
                  }
                : undefined,
            refinementList: configure.value.refinementList ? Object.fromEntries(configure.value.refinementList) : undefined,
            limit,
        },
    }));
</script>
<template>
    <org-algolia-search-result-products
        :hide-age-facet="isHiddenOption(data.filter?.age)"
        :hide-colors-facet="isHiddenOption(data.filter?.color)"
        :hide-height-facet="isHiddenOption(data.filter?.height)"
        :hide-hierarchy-menu="isHiddenOption(data.filter?.product_category)"
        :hide-sort="isHiddenOption(data.sort)"
        :hide-wheels-facet="isHiddenOption(data.filter?.wheels)"
        :index-name="activeIndex"
        :initial-ui-state="initialUiState"
        class="org-algolia-search-result-products">
        <ais-configure
            :hits-per-page.camel="limit"
            v-bind="{ numericFilters }" />
    </org-algolia-search-result-products>
</template>
